/**
 * QAs - every question on the matter, in ONE list.
 *
 * It used to be three lists behind Unanswered / Answered / Completed tabs, so
 * "what is outstanding on this matter" meant visiting three places and holding
 * the counts in your head, and a question that got answered silently vanished
 * from the list you were working down. The state was never worth a navigation:
 * it is one fact about a row.
 *
 * So the state is now carried twice, on the row itself:
 *   • the glyph tone - neutral/dashed = pending, champagne = answered,
 *     success = completed (`RowGlyph`, unchanged);
 *   • a trailing STATUS cell - the same tri-state as a badge with a matching
 *     coloured dot, so the right-hand edge of the list reads as a column.
 */

import React, { useState, useMemo } from 'react';
import { getIntlLocale } from '../../../utils/functions';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../store';
import Button from '../../shared/TailwindComponents/Button';
import Badge from '../../shared/TailwindComponents/Badge';
import { Well } from '../../shared/brand/Field';
import FilterBarWithTabs from '../../shared/TailwindComponents/FilterBarWithTabs';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import Table from '../../shared/TailwindComponents/Table';
import {
  PiQuestionMarkLight,
  PiChatCircleTextLight,
  PiPlusLight,
  PiCheckCircleLight,
  PiUserPlusLight,
  PiSparkleLight
} from 'react-icons/pi';
import { MatterData } from '../MatterDetails';
import { BadgeColor } from '../../shared/TailwindComponents/types/badgeTypes';
import clsx from 'clsx';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import { useGenerateMore } from '../../../hooks/useGenerateMore';
import FindMoreProgress from '../FindMoreProgress';
import SubTabBar from '../../shared/TailwindComponents/SubTabBar';
import { MetaDot, RowGlyph } from '../../shared/surface/ListRow';
import { useMatterSurface } from '../surface/MatterSurfaceContext';
import { questionId } from '../surface/detail/MatterItemDetail';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, qasItemsTourSteps } from '../../shared/FeatureTour';

/** The tri-state a question is in. The ONE derivation of it. */
export type QuestionState = 'pending' | 'answered' | 'completed';

export const questionState = (question: any): QuestionState => {
  if (question?.status === 'finalized') return 'completed';
  return question?.potential_answer ? 'answered' : 'pending';
};

/** Sort weight - outstanding work first. */
const QUESTION_RANK: Record<QuestionState, number> = {
  pending: 0,
  answered: 1,
  completed: 2,
};

const questionRank = (question: any): number => QUESTION_RANK[questionState(question)];

/** Status cell presentation. White/neutral → gold → green, as the row glyph. */
const STATE_PRESENTATION: Record<QuestionState, {
  label: string;
  badge: BadgeColor;
  glyph: 'pending' | 'accent' | 'done';
  dot: string;
}> = {
  pending: { label: 'Pending', badge: 'gray', glyph: 'pending', dot: 'bg-fg-muted/40' },
  answered: { label: 'Answered', badge: 'amber', glyph: 'accent', dot: 'bg-champagne' },
  completed: { label: 'Completed', badge: 'green', glyph: 'done', dot: 'bg-success-500' },
};

interface QAsTabContentProps {
  matterData: MatterData | null;
  questionnaire: any[];
  loading: boolean;
  userAccessLevel: 'edit' | 'admin' | 'view';
  onUpdate: () => void;
  hasDocuments?: boolean;
}

const QAsTabContent: React.FC<QAsTabContentProps> = ({
  matterData,
  questionnaire,
  loading,
  userAccessLevel,
  onUpdate,
  hasDocuments = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const canEdit = userAccessLevel === 'edit' || userAccessLevel === 'admin';
  const surface = useMatterSurface();

  /** Add → the shared container, empty, in create mode. */
  const openCreate = () => {
    if (!canEdit) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: t("You don't have access to add a question."),
        modalType: 'error',
        duration: 3000,
      }));
      return;
    }
    surface?.select({ kind: 'qa', id: null, mode: 'create' });
  };
  const matterUuid = matterData?.uuid || matterData?.MatterID || '';

  const [searchTerm, setSearchTerm] = useState('');

  const { isGenerating: isFindingMore, handleGenerateMore: handleFindMoreQuestions, progress: findMoreProgress } = useGenerateMore({
    matterUuid,
    targetStep: 'questions',
    onComplete: onUpdate,
  });

  const {
    isRunning: isItemsTourRunning,
    handleJoyrideCallback: handleItemsTourCallback,
  } = useFeatureTour({
    tourId: 'qas-items',
    steps: qasItemsTourSteps,
    readyToShow: questionnaire.length > 0 && !loading,
    autoStartDelay: 600,
  });

  /**
   * ONE list, newest activity first, pending questions ahead of settled ones.
   *
   * Removing the tabs removed the only thing that kept outstanding work at the
   * top, so the sort carries it: pending, then answered, then completed, and
   * within each the most recently touched first.
   */
  const filteredQuestionnaire = useMemo(() => {
    const filtered = questionnaire.filter((question: any) => {
      const term = searchTerm.toLowerCase();
      return question.question.toLowerCase().includes(term)
        || (question.potential_answer && question.potential_answer.toLowerCase().includes(term));
    });

    return filtered.sort((a: any, b: any) => {
      const rank = questionRank(a) - questionRank(b);
      if (rank !== 0) return rank;
      const aDate = new Date(a.when_answered || a.created_at || '1970-01-01').getTime();
      const bDate = new Date(b.when_answered || b.created_at || '1970-01-01').getTime();
      return bDate - aDate;
    });
  }, [questionnaire, searchTerm]);

  const completedCount = questionnaire.filter((q: any) => q.status === 'finalized').length;

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString(getIntlLocale(), {
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  // ── The shared Table (UI feedback M-15): Q&A renders on the same table as
  //    Documents/Clients/etc., keeping its own open logic (a row click opens the
  //    surface detail pane) and the trailing Status column. The question cell is
  //    rich - glyph, question, and a meta line - but it is still one table cell.
  const QUESTION_COL = t('Question');
  const STATUS_COL = t('Status');
  const columns = [QUESTION_COL, STATUS_COL];

  const renderQaCell = (column: string, _value: any, row: any): React.ReactNode => {
    const question = row._raw;
    const id = questionId(question);
    const state = questionState(question);
    const look = STATE_PRESENTATION[state];
    const isOpen = surface?.selection?.kind === 'qa' && surface.selection.id === id;

    if (column === QUESTION_COL) {
      return (
        <div className="flex items-start gap-2 min-w-0 py-0.5">
          <span className="flex-shrink-0">
            <RowGlyph state={look.glyph}>
              {state === 'completed'
                ? <PiCheckCircleLight className="h-4 w-4" />
                : state === 'answered'
                  ? <PiChatCircleTextLight className="h-3.5 w-3.5" />
                  : <PiQuestionMarkLight className="h-3.5 w-3.5" />}
            </RowGlyph>
          </span>
          <div className="min-w-0">
            <p
              className={clsx(
                'text-[13px] truncate leading-snug',
                isOpen ? 'font-medium text-champagne' : 'font-medium text-fg-body'
              )}
              title={question.question}
            >
              {question.question}
            </p>
            <p className="flex items-center gap-1 text-[11px] text-fg-muted truncate mt-0.5">
              {question.client_assigned && (
                <>
                  <span className="inline-flex items-center gap-1 text-champagne">
                    <PiUserPlusLight className="h-3 w-3" aria-hidden />
                    {t('Client')}
                  </span>
                  <MetaDot />
                </>
              )}
              {question.potential_answer
                ? <span className="truncate">{question.potential_answer}</span>
                : <span>{t('No answer yet')}</span>}
              {question.when_answered && (
                <><MetaDot /><span className="flex-shrink-0">{formatDate(question.when_answered)}</span></>
              )}
            </p>
          </div>
        </div>
      );
    }

    if (column === STATUS_COL) {
      return (
        <Badge color={look.badge} className="!text-[10px] !px-1.5 !py-0 whitespace-nowrap">
          <span
            aria-hidden
            className={clsx('me-1 inline-block h-1.5 w-1.5 rounded-full align-middle', look.dot)}
          />
          {t(look.label)}
        </Badge>
      );
    }
    return undefined;
  };

  const renderCurrentTab = () => {
    if (filteredQuestionnaire.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center text-center py-14 px-6">
          <div className="h-12 w-12 rounded-full bg-surface-sunken flex items-center justify-center mb-3">
            <PiQuestionMarkLight className="h-6 w-6 text-champagne" />
          </div>
          <h3 className="text-fg-body font-medium mb-1">
            {searchTerm ? t('No matching questions') : t('No questions yet')}
          </h3>
          <p className="text-sm text-fg-muted max-w-md">
            {searchTerm
              ? t('Try adjusting your search.')
              : t('Add a question to gather what this matter still needs, or generate them from the case files.')}
          </p>
          {canEdit && !searchTerm && (
            <div className="flex items-center gap-2 mt-4">
              <Button variant="outline" size="small" icon={<PiPlusLight className="h-4 w-4" />} onClick={openCreate}>
                {t('Add question')}
              </Button>
              <Button
                variant="primary"
                size="small"
                onClick={() => handleFindMoreQuestions()}
                disabled={!hasDocuments}
                tooltip={!hasDocuments ? t('Upload at least one case file to enable AI generation.') : ''}
                icon={<PiSparkleLight className="h-3.5 w-3.5" />}
              >
                {t('Generate')}
              </Button>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <div data-tour-id="qas-context-hint">
          <Well className="text-xs text-fg-muted">
            {completedCount > 0
              ? t('{{count}} completed question(s) are included in the matter context for drafting and insights.', { count: completedCount })
              : t('Only completed questions are included in the matter context.')}
          </Well>
        </div>

        <Table
          tableId="matter-qas"
          columns={columns}
          data={filteredQuestionnaire.map((question) => {
            const id = questionId(question);
            const isOpen = surface?.selection?.kind === 'qa' && surface.selection.id === id;
            return {
              id,
              [QUESTION_COL]: question.question,
              [STATUS_COL]: questionState(question),
              // `Table` honours a row's own className, which is how the open
              // row gets its highlight without a checkbox column (same pattern
              // as MatterTasksTable).
              className: isOpen ? 'bg-champagne/10' : undefined,
              _raw: question,
            };
          })}
          renderCell={renderQaCell}
          onRowClick={(row: any) => surface?.select({ kind: 'qa', id: questionId(row._raw), mode: 'edit' })}
          min="min-h-[16rem]"
          max="max-h-[calc(100vh-360px)]"
          defaultColumnWidths={{ [STATUS_COL]: 140 }}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="animate-fadePulse bg-surface-sunken h-8 w-48 rounded" />
          <div className="animate-fadePulse bg-surface-sunken h-8 w-24 rounded" />
        </div>
        <Skeleton variant="table" rows={5} />
      </div>
    );
  }

  if (!matterData) {
    return (
      <div className="p-6 text-center text-fg-muted">
        {t('Loading questionnaire...')}
      </div>
    );
  }

  return (
    <div className="space-y-2">

      {/* One tab, and it exists only to carry the actions - the state split
          that used to live here is now a column on every row. */}
      <div data-tour-id="qas-sub-tabs">
      <SubTabBar
        tabs={[{
          id: 'qas',
          label: t('Q&As'),
          icon: <PiQuestionMarkLight className="h-3 w-3" />,
          count: questionnaire.length,
          color: 'blue' as BadgeColor,
        }]}
        activeTab="qas"
        onTabChange={() => {}}
        actions={canEdit && !isFindingMore ? (
          <>
            <Button
              variant="outline"
              size="small"
              onClick={openCreate}
            >
              {t('Add question')}
            </Button>
            <Button
              variant="primary"
              size="small"
              onClick={handleFindMoreQuestions}
              disabled={!hasDocuments}
              tooltip={!hasDocuments ? t('Upload at least one case file to enable AI generation.') : ''}
              icon={<PiSparkleLight className="h-3.5 w-3.5" />}
            >
              {questionnaire.length === 0 ? t('Generate') : t('Find more')}
            </Button>
          </>
        ) : undefined}
      />
      </div>

      {isFindingMore || findMoreProgress.status === 'complete' || findMoreProgress.status === 'failed' ? (
        <FindMoreProgress progress={findMoreProgress} isGenerating={isFindingMore} />
      ) : (
        <div>
          <FilterBarWithTabs
            onSearchChange={setSearchTerm}
            noParentPadding={true}
            noParentHeight={true}
            itemCount={filteredQuestionnaire.length}
            totalItemCount={questionnaire.length}
            itemCountLabel={questionnaire.length === 1 ? 'question' : 'questions'}
          />

          <div data-tour-id="qas-table-area" className="p-0 min-h-[250px]">
            {renderCurrentTab()}
          </div>
        </div>
      )}
      <FeatureTour
        steps={qasItemsTourSteps}
        run={isItemsTourRunning}
        onCallback={handleItemsTourCallback}
        disableOverlay
      />
    </div>
  );
};

export default QAsTabContent;
